import { useEffect, useRef } from 'react';

type WakeLockSentinelLike = {
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
};

type NavigatorWithWakeLock = Navigator & {
  wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinelLike>;
  };
};

/**
 * Requests a screen wake lock while a scan is active (web equivalent of KeepAwake).
 * Silently no-ops when Wake Lock API is unavailable or permission is denied.
 */
export function useScanWakeLock(enabled: boolean): void {
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);

  useEffect(() => {
    const nav = navigator as NavigatorWithWakeLock;
    if (!enabled || typeof navigator === 'undefined' || !nav.wakeLock) {
      return;
    }

    let cancelled = false;

    const request = async () => {
      try {
        const sentinel = await nav.wakeLock!.request('screen');
        if (cancelled) {
          void sentinel.release();
          return;
        }
        sentinelRef.current = sentinel;
        sentinel.addEventListener('release', () => {
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null;
          }
        });
      } catch {
        // Permission denied / unsupported document visibility — ignore.
      }
    };

    void request();

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && enabled) {
        void request();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      if (sentinel) {
        void sentinel.release().catch(() => undefined);
      }
    };
  }, [enabled]);
}
